import {
  invalidateAdminCompanyTreeQueryData,
  useGetApiAdminJobStatus,
  usePostAdminTriggersPackages,
} from '@sit/client-shared';
import { skipToken, useQueryClient } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import Heading from 'carbon-react/lib/components/heading';
import Pod from 'carbon-react/lib/components/pod';
import { useState } from 'react';
import toastr from '../../helpers/toastr';

interface BulkActionsProps {
  companyTreeSettingsIds: number[];
}

export function BulkActions({ companyTreeSettingsIds }: BulkActionsProps) {
  const disabled = companyTreeSettingsIds.length === 0;
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | undefined>();
  useGetApiAdminJobStatus(
    jobId
      ? {
          queryParams: {
            jobId,
            queueName: 'update-triggers-packages',
          },
          requestConfig: {
            skipToast: true,
          },
        }
      : skipToken,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: 2_000,
      retry: (failureCount, error) => {
        if (error?.status === 404) {
          toastr.success('Update company triggers package job complete');
          setJobId(undefined);
          void invalidateAdminCompanyTreeQueryData(queryClient);
          return false;
        }
        if (!(error instanceof Error)) {
          return false;
        }
        if (error.message?.includes('Too many requests') || failureCount < 5) {
          return true;
        }
        toastr.error(error.message);
        return false;
      },
    },
  );
  const { mutate: updateTriggersPackages } = usePostAdminTriggersPackages();

  const startPackageUpdates = () => {
    updateTriggersPackages(
      {
        body: {
          companyTreeSettingsIds,
        },
      },
      {
        onSuccess: (data) => {
          setJobId(data.jobId);
          toastr.info('Triggers package update started.');
        },
        onError: (error) => {
          toastr.error(error.message);
        },
      },
    );
  };
  return (
    <>
      <Box pt={2}>
        <Pod>
          <Heading headingType="h3" title="Bulk actions" />
          <Box display="flex" flexDirection="row" gap={2}>
            <Button
              disabled={disabled}
              buttonType="secondary"
              onClick={startPackageUpdates}
            >
              Update trigger package
            </Button>
          </Box>
        </Pod>
      </Box>
    </>
  );
}
